import {
    ContainerRef,
    EntityFactory,
    EntityType,
    PageDraft
} from "@amzn/ask-legal-domain";
import { Alert, Button, Flashbar, SpaceBetween } from "@amzn/awsui-components-react";
import { PageContentEvaluation } from "../page/PageContentEvaluation";
import * as React from "react";
import { callApi } from "../../utils/api-call";
import { PageDraftModel } from "../../model/page-draft-model";
import { UIModel } from "../../model/ui-model";
import { AppContext } from "../../setup/context";
import { CustomCommonContainerModal } from "../container/CommonContainerModal";
import { PageSectionV2 } from "../page-section/PageSectionCompV2";
import { PageBaseLayoutV2 } from "../page/PageBaseLayoutV2";
import { PageDraftApprovalFlowNew } from "./PageDraftApprovalFlowNew";
import { PageDraftGeneralInfoV2 } from "./PageDraftGeneralInfoV2";
import { EditContainerContent } from "../container/modal-content/EditContainerContent";
import { StringUtils } from "../../utils/common-utils";
import { getContainerHeader } from "../../utils/container-utils";

/**
 * Draft view for the PathFinder Agentic UI lab. Same behavior as PageDraftDetail.Comp
 * (approval flow, add / edit / reorder containers, compare with live); uses the V2 layout,
 * section and container cards shared with the live page. Loading is still done by
 * PageDraftDetail.Preload.
 */
export namespace PageDraftDetailV2 {
    export const Comp = (props: {
        state: UIModel.State<PageDraft.Data>;
        setIsEditing: (isEditing: boolean) => void;
        liveContainerMap?: Map<string, ContainerRef>;
    }) => {
        const context = React.useContext(AppContext);
        const pageBottomRef = React.useRef<HTMLDivElement>(null);
        const reorderContainerState = PageDraftModel.ReorderContainerState.use({
            draft: props.state.value
        });
        const addContainerState = PageDraftModel.EditContainerState.use({
            draftRef: EntityFactory.toEntityRef(props.state.value)
        });

        const [isReordering, setIsReordering] = React.useState<boolean>(false);
        const [isAddingContainer, setIsAddingContainer] = React.useState<boolean>(false);
        // Set when a Main container was just added; the page scrolls to it once the sections update
        const scrollToBottomPendingRef = React.useRef<boolean>(false);
        const isMountedRef = React.useRef<boolean>(true);

        React.useEffect(() => {
            isMountedRef.current = true;
            return () => { isMountedRef.current = false; };
        }, []);
        const editingContainerIdState = UIModel.State.use<string>({
            initialValue: ""
        });

        // Same as PageDraftDetail: reorder / add errors are not shown, the controls just stop loading
        const reorder = async () => {
            setIsReordering(true);
            const result = await callApi(
                context.getPageDraftAPI().updateOrder,
                PageDraftModel.ReorderContainerState.toInput(reorderContainerState)
            );
            if (!isMountedRef.current) return;
            setIsReordering(false);
            if (result.ok) {
                reorderContainerState.setDraft(result.data);
            }
        };
        const addContainer = async () => {
            const location = addContainerState.locationField.value;
            setIsAddingContainer(true);
            const resultPromise = callApi(
                context.getPageDraftAPI().addContainer,
                PageDraftModel.EditContainerState.toAddContainerInput(addContainerState)
            );
            navigator.clipboard?.writeText("").catch(() => undefined);
            const result = await resultPromise;
            if (!isMountedRef.current) return;
            setIsAddingContainer(false);
            if (result.ok) {
                scrollToBottomPendingRef.current = location === "Main";
                addContainerState.activeContainerTypeField.setValue(null);
                reorderContainerState.setDraft(result.data);
            }
        };

        const scrollToBottomOfPage = () => {
            if (scrollToBottomPendingRef.current) {
                scrollToBottomPendingRef.current = false;
                pageBottomRef.current?.scrollIntoView({ behavior: "smooth" });
            }
        };

        const [customSize, setCustomSize] = React.useState<"sm" | "lg" | "xl" | "xxl">("lg");

        // Stable reference: a new object on every render made PageContentEvaluation re-fetch each render
        const contentEvaluationDraftRef = React.useMemo(() => ({
            id: props.state.value.id,
            version: props.state.value.version,
            type: EntityType.PageDraft
        }), [props.state.value.id, props.state.value.version]);

        React.useEffect(() => {
            props.state.setValue(reorderContainerState.draftField.value);
        }, [
            reorderContainerState.draftField.value.id,
            reorderContainerState.draftField.value.version
        ]);

        React.useEffect(() => {
            switch (addContainerState.activeContainerTypeField.value) {
                default: setCustomSize("xxl");
            }
        }, [addContainerState.activeContainerTypeField.value]);

        React.useEffect(scrollToBottomOfPage, [reorderContainerState.newOrderedSectionsField.value]);

        React.useEffect(() => {
            props.setIsEditing(
                !StringUtils.isEmpty(editingContainerIdState.value) ||
                !!addContainerState.activeContainerTypeField.value
            );
        }, [editingContainerIdState.value, addContainerState.activeContainerTypeField.value]);

        React.useEffect(() => {
            // stop edit mode when component unmounts
            return () => { props.setIsEditing(false); };
        }, []);

        /** Check if this draft was created from a historical version. */
        const isFromHistoricalVersion = props.state.value.sourcePageVersion != null;

        return (
            <React.Fragment>
                {!!addContainerState.activeContainerTypeField.value && (
                    <CustomCommonContainerModal
                        header={getContainerHeader("Add", addContainerState.activeContainerTypeField.value)}
                        loading={isAddingContainer}
                        child={
                            <EditContainerContent state={addContainerState} />
                        }
                        onCancel={() => {
                            addContainerState.activeContainerTypeField.setValue(null);
                            navigator.clipboard?.writeText("").catch(() => undefined);
                        }}
                        onSave={addContainer}
                        size={customSize}
                        disabled={!addContainerState.validState}
                    />
                )}
                <SpaceBetween direction="vertical" size="s">
                    <PageDraftApprovalFlowNew
                        state={props.state}
                        isEditing={
                            !StringUtils.isEmpty(editingContainerIdState.value) ||
                            !!addContainerState.activeContainerTypeField.value
                        }
                    />
                    {isFromHistoricalVersion && (
                        <Alert type="info">
                            This draft was created from a previous published version (Version {props.state.value.sourcePageVersion}).
                        </Alert>
                    )}
                    <PageContentEvaluation
                        draftRef={contentEvaluationDraftRef}
                        pageId={props.state.value.pageId}
                    />
                    <PageDraftGeneralInfoV2 state={props.state}/>
                    {reorderContainerState.isDirty() && <Flashbar items={[{
                        type: "warning",
                        content: "You just changed the order of containers, click Save button to commit the change",
                        dismissible: false,
                        action: (
                            <SpaceBetween direction="horizontal" size="s">
                                <Button
                                    loading={isReordering}
                                    onClick={() => reorderContainerState.reset()}>Reset</Button>
                                <Button
                                    loading={isReordering}
                                    variant="primary" onClick={reorder}>Save</Button>
                            </SpaceBetween>
                        )
                    }]} />}
                    {props.state.value.status === "DRAFT" ? (
                        <PageBaseLayoutV2
                            mainContent={
                                <PageSectionV2.Edit
                                    location="Main"
                                    activeEditContainerState={editingContainerIdState}
                                    updateOrderState={reorderContainerState}
                                    addContainerState={addContainerState}
                                    pageBottomRef={pageBottomRef}
                                    liveContainerMap={props.liveContainerMap}
                                />
                            }
                            rightContent={
                                <PageSectionV2.Edit
                                    location="Right"
                                    activeEditContainerState={editingContainerIdState}
                                    updateOrderState={reorderContainerState}
                                    addContainerState={addContainerState}
                                    liveContainerMap={props.liveContainerMap}
                                />
                            }
                        />) : (
                            <PageBaseLayoutV2
                                mainContent={(() => {
                                    const section = props.state.value.newSections.find(s => s.location === "Main");
                                    return section ? <PageSectionV2.View section={section} draftView={true} liveContainerMap={props.liveContainerMap} /> : <></>;
                                })()}
                                rightContent={(() => {
                                    const section = props.state.value.newSections.find(s => s.location === "Right");
                                    return section ? <PageSectionV2.View section={section} liveContainerMap={props.liveContainerMap} /> : <></>;
                                })()}
                            />
                        )
                    }
                </SpaceBetween>
            </React.Fragment>
        );
    };
}
